/**
 * Interoperability Feature — Centralized constants
 *
 * Route names, option lists, and mock configuration values used across the
 * Interoperability module. Centralizing these avoids repeating raw strings across
 * components and keeps role/status/trigger option lists as a single source
 * of truth.
 */
import type {
  InteroperabilityRole,
  InteroperabilityUserStatus,
  MappingRuleType,
  PipelineStageKey,
  ValueMatchType,
} from './types';
import { INTEROPERABILITY_ROLE_VALUES, INTEROPERABILITY_USER_STATUS_VALUES, PIPELINE_STAGE_KEY_VALUES, VALUE_MATCH_TYPE_VALUES } from './types';

// ── Route names ──────────────────────────────────────────────────────────────

export const INTEROPERABILITY_ROUTE_NAMES = {
  userManagement: 'interoperability-user-management',
  fieldMapping: 'interoperability-field-mapping',
  valueMapping: 'interoperability-value-mapping',
  transactionMonitoring: 'interoperability-transaction-monitoring',
} as const;

export const INTEROPERABILITY_ENTITLEMENT = 'INTEROPERABILITY';

// ── Role / status option lists (immutable) ─────────────────────────────────

export const INTEROPERABILITY_ROLES: readonly InteroperabilityRole[] = INTEROPERABILITY_ROLE_VALUES;
export const INTEROPERABILITY_USER_STATUSES: readonly InteroperabilityUserStatus[] = INTEROPERABILITY_USER_STATUS_VALUES;
export const VALUE_MATCH_TYPES: readonly ValueMatchType[] = VALUE_MATCH_TYPE_VALUES;
export const PIPELINE_STAGE_KEYS: readonly PipelineStageKey[] = PIPELINE_STAGE_KEY_VALUES;

/** English fallback labels used with `t(key, fallback)` — see interoperability.labels.ts. */
export const MAPPING_RULE_TYPE_LABELS: Record<MappingRuleType, string> = {
  DIRECT_STANDARD: 'Direct (Standard)',
  DIRECT_OVERRIDE: 'Direct — Override',
  FORMAT_TRANSFORM: 'Format Transform',
  CONSTANT: 'Constant',
  CROSSWALK: 'Crosswalk',
};

// ── Channels available for scoping mappings ────────────────────────────────

// ── Mock external links (never inline these in templates) ─────────────────

/** Placeholder Interoperability Administrator user-guide URL — replace once a real link is available. */
export const INTEROPERABILITY_USER_GUIDE_URL = 'https://docs.interoperability.example.com/administrator/user-guide';

// ── Mock service simulated latency ─────────────────────────────────────────

/** Small artificial delay so loading states are visible in the mock services. */
export const MOCK_SERVICE_DELAY_MS = 250;
